/**
 * Operator Dashboard — client-side mock data.
 *
 * This is the single source of truth for mock data in the frontend.
 * It mirrors the mock served by hub-api/src/modules/operator/mock.ts.
 *
 * The arrays are intentionally mutable so that action functions in hubApi.ts
 * can update them in-memory for the current session — no persistence, no
 * localStorage/sessionStorage/cookies.
 *
 * To switch from mock to real HTTP calls, change HUB_API_MODE in hubApi.ts
 * and provide the base URL.  No changes to App.tsx or components are needed.
 */

import type {
  BotScenario,
  DashboardKpi,
  OperatorChannel,
  OperatorClientCard,
  OperatorDialog,
  OperatorMessage,
  RoutingTimeline,
  TelegramDemoResult,
} from './types';

// ─── Channels ────────────────────────────────────────────────────────────────

export const MOCK_CHANNELS: OperatorChannel[] = [
  { id: 'ch-max-1',  name: 'MAX',      code: 'max',      enabled: true, hint: 'приоритет 1',        tone: 'max' },
  { id: 'ch-tg-1',   name: 'Telegram', code: 'telegram', enabled: true, hint: 'быстрый тест',       tone: 'telegram' },
  { id: 'ch-wa-1',   name: 'WhatsApp', code: 'whatsapp', enabled: true, hint: 'через Business API', tone: 'whatsapp' },
  { id: 'ch-vk-1',   name: 'VK',       code: 'vk',       enabled: true, hint: 'сообщество',         tone: 'vk' },
];

// ─── KPI ─────────────────────────────────────────────────────────────────────

export function getMockKpi(): DashboardKpi {
  return {
    dialogsToday: 42,
    avgFirstResponseTime: '03:18',
    waitingWithoutReply: 3,
    operatorLoadPercent: 68,
    calculatedAt: new Date().toISOString(),
  };
}

// ─── Dialogs (mutable) ───────────────────────────────────────────────────────

export const MOCK_DIALOGS: OperatorDialog[] = [
  {
    id: 'D-1042',
    clientName: 'Елена К.',
    phone: '+7 916 234-45-67',
    channel: 'telegram',
    shopName: 'м. Солнцево',
    statusLabel: 'Ожидает ответа',
    status: 'queued',
    source: 'zov-mebel.ru / utm: cpc',
    elapsedLabel: '2 мин',
    assignedOperatorName: 'Ирина П.',
    unreadCount: 2,
    updatedAt: new Date(Date.now() - 2 * 60_000).toISOString(),
    createdAt: new Date(Date.now() - 2 * 60_000).toISOString(),
    slaStatus: 'ok' as const,
  },
  {
    id: 'D-1041',
    clientName: 'Алексей М.',
    phone: '+7 925 800-13-21',
    channel: 'max',
    shopName: 'м. Бибирево',
    statusLabel: 'В работе',
    status: 'assigned',
    source: 'grace-mebel.ru / organic',
    elapsedLabel: '8 мин',
    assignedOperatorName: 'Олег С.',
    unreadCount: 0,
    updatedAt: new Date(Date.now() - 8 * 60_000).toISOString(),
    createdAt: new Date(Date.now() - 8 * 60_000).toISOString(),
    assignedAt: new Date(Date.now() - 7 * 60_000).toISOString(),
    slaStatus: 'ok' as const,
  },
  {
    id: 'D-1040',
    clientName: 'Марина',
    phone: '+7 903 777-88-11',
    channel: 'whatsapp',
    shopName: 'м. Домодедовская',
    statusLabel: 'Передан',
    status: 'escalated',
    source: 'zov-kuhni.ru / direct',
    elapsedLabel: '14 мин',
    assignedOperatorName: 'Руководитель магазина',
    unreadCount: 1,
    updatedAt: new Date(Date.now() - 14 * 60_000).toISOString(),
    createdAt: new Date(Date.now() - 14 * 60_000).toISOString(),
  },
  {
    id: 'D-1039',
    clientName: 'Сергей Т.',
    phone: '+7 999 120-45-10',
    channel: 'vk',
    shopName: 'Отдел сервиса',
    statusLabel: 'Закрыт',
    status: 'closed',
    source: 'vk.com / referral',
    elapsedLabel: '31 мин',
    assignedOperatorName: 'Сервис',
    unreadCount: 0,
    updatedAt: new Date(Date.now() - 31 * 60_000).toISOString(),
    createdAt: new Date(Date.now() - 31 * 60_000).toISOString(),
    slaStatus: 'ok' as const,
  },
];

// ─── Messages (mutable) ──────────────────────────────────────────────────────

export const MOCK_MESSAGES: OperatorMessage[] = [
  { id: 'm-1', dialogId: 'D-1042', senderType: 'bot',      text: 'Здравствуйте. Выберите, куда направить обращение: магазин или отдел сервиса.',  timeLabel: '12:04', createdAt: '2025-01-01T12:04:00Z' },
  { id: 'm-2', dialogId: 'D-1042', senderType: 'client',   text: 'Магазин. Хочу уточнить стоимость кухни по проекту.',                            timeLabel: '12:05', createdAt: '2025-01-01T12:05:00Z' },
  { id: 'm-3', dialogId: 'D-1042', senderType: 'bot',      text: 'Выберите магазин из списка.',                                                    timeLabel: '12:05', createdAt: '2025-01-01T12:05:30Z' },
  { id: 'm-4', dialogId: 'D-1042', senderType: 'client',   text: 'м. Солнцево. Телефон +7 916 234-45-67, Елена.',                            timeLabel: '12:06', createdAt: '2025-01-01T12:06:00Z' },
  { id: 'm-5', dialogId: 'D-1042', senderType: 'operator', text: 'Елена, добрый день. Сейчас посмотрю ваш проект и вернусь с расчётом.',           timeLabel: '12:07', createdAt: '2025-01-01T12:07:00Z' },
];

export function getMockMessages(dialogId: string): OperatorMessage[] {
  return MOCK_MESSAGES.filter((m) => m.dialogId === dialogId);
}

// ─── Client cards ─────────────────────────────────────────────────────────────

const MOCK_CLIENT_CARDS: OperatorClientCard[] = [
  { dialogId: 'D-1042', phone: '+7 916 234-45-67', name: 'Елена К.',   shopName: 'м. Солнцево', source: 'zov-mebel.ru / utm: cpc',  sourceLabel: 'Сайт: https://zov-mebel.ru',  pageUrl: 'https://zov-mebel.ru',  assignedOperatorName: 'Ирина П.',             crmFound: false, crmClientId: null },
  { dialogId: 'D-1041', phone: '+7 925 800-13-21', name: 'Алексей М.', shopName: 'м. Бибирево',         source: 'grace-mebel.ru / organic', sourceLabel: 'Сайт: https://grace-mebel.ru', pageUrl: 'https://grace-mebel.ru', assignedOperatorName: 'Олег С.',              crmFound: false, crmClientId: null },
  { dialogId: 'D-1040', phone: '+7 903 777-88-11', name: 'Марина',      shopName: 'м. Домодедовская',  source: 'zov-kuhni.ru / direct',    sourceLabel: 'Сайт: https://zov-kuhni.ru', pageUrl: 'https://zov-kuhni.ru', assignedOperatorName: 'Руководитель магазина', crmFound: false, crmClientId: null },
  { dialogId: 'D-1039', phone: '+7 999 120-45-10', name: 'Сергей Т.',  shopName: 'Отдел сервиса',     source: 'vk.com / referral',        sourceLabel: 'VK',                                               pageUrl: 'https://vk.com',        assignedOperatorName: 'Сервис',               crmFound: false, crmClientId: null },
];

export function getMockClientCard(dialogId: string): OperatorClientCard | undefined {
  return MOCK_CLIENT_CARDS.find((c) => c.dialogId === dialogId);
}

// ─── Routing timeline ────────────────────────────────────────────────────────

export function getMockRoutingTimeline(dialogId: string): RoutingTimeline {
  return {
    dialogId,
    steps: [
      { order: 1, title: 'Менеджер выбранного магазина',   detail: 'Смена открыта, рабочее время',                                             state: 'active' },
      { order: 2, title: 'Руководитель магазина',           detail: 'Если менеджер не ответит в заданное время',                               state: 'next' },
      { order: 3, title: 'Учредитель / ответственный',      detail: 'Если роль есть в справочнике; иначе уровень пропускается',               state: 'skip' },
      { order: 4, title: 'Другой работающий магазин',       detail: 'Предлагается клиенту при отсутствии ответа',                              state: 'next' },
      { order: 5, title: 'Форма обратной связи',            detail: 'Финальный сценарий "Мы свяжемся позже"',                                 state: 'next' },
    ],
  };
}

// ─── Bot scenario ─────────────────────────────────────────────────────────────

export const MOCK_BOT_SCENARIO: BotScenario = {
  id: 'mvp-7day-pilot',
  name: 'Бот без ИИ — MVP сценарий 7-дневного теста',
  steps: [
    { order: 1, label: 'Приветствие: «Это ваш интерактивный помощник»',  stateKey: 'start' },
    { order: 2, label: 'Выбор: Магазин / Отдел сервиса',                  stateKey: 'choose_department' },
    { order: 3, label: 'Выбор магазина (8 точек, м. Солнцево → CRM 56)',  stateKey: 'choose_shop' },
    { order: 4, label: 'Телефон → lookup в CRM по calculation/client',    stateKey: 'request_phone' },
    { order: 5, label: 'Имя (пропускается, если есть в CRM)',             stateKey: 'request_name' },
    { order: 6, label: 'Только сервис: «Опишите ваш вопрос»',             stateKey: 'request_service_question' },
    { order: 7, label: 'Магазин: «Передаю обращение…», busy-timer 30 с',  stateKey: 'handoff' },
    { order: 8, label: 'Сервис: «Передаю обращение в отдел сервиса»',     stateKey: 'handoff' },
  ],
};

// ─── Audit log & outbox (in-memory) ─────────────────────────────────────────

export interface MockAuditEvent {
  id: string;
  dialogId: string;
  action: string;
  occurredAt: string;
  meta: Record<string, unknown>;
}

export interface MockOutboxMessage {
  id: string;
  dialogId: string;
  messageId: string;
  status: 'queued' | 'mock_sent';
  createdAt: string;
}

let _auditSeq = 0;
let _outboxSeq = 0;
let _msgSeq = 100;

export const MOCK_AUDIT_LOG: MockAuditEvent[] = [];
export const MOCK_OUTBOX: MockOutboxMessage[] = [];

export function createMockAuditEvent(
  dialogId: string,
  action: string,
  meta: Record<string, unknown>
): MockAuditEvent {
  const evt: MockAuditEvent = {
    id: `audit-${++_auditSeq}`,
    dialogId,
    action,
    occurredAt: new Date().toISOString(),
    meta,
  };
  MOCK_AUDIT_LOG.push(evt);
  return evt;
}

function createMockOutbox(dialogId: string, messageId: string): MockOutboxMessage {
  const entry: MockOutboxMessage = {
    id: `outbox-${++_outboxSeq}`,
    dialogId,
    messageId,
    status: 'mock_sent',
    createdAt: new Date().toISOString(),
  };
  MOCK_OUTBOX.push(entry);
  return entry;
}

// ─── Mock action helpers ─────────────────────────────────────────────────────

function requireDialog(dialogId: string): OperatorDialog {
  const d = MOCK_DIALOGS.find((x) => x.id === dialogId);
  if (!d) throw new Error(`dialog_not_found:${dialogId}`);
  return d;
}

export function mockAssign(dialogId: string, operatorName: string): OperatorDialog {
  const dialog = requireDialog(dialogId);
  const now = new Date();
  dialog.status = 'assigned';
  dialog.statusLabel = 'В работе';
  dialog.assignedOperatorName = operatorName;
  dialog.updatedAt = now.toISOString();
  dialog.assignedAt = now.toISOString();
  dialog.slaStatus = 'ok';
  createMockAuditEvent(dialogId, 'dialog.assigned', { operatorName });
  return { ...dialog };
}

export function mockSendMessage(dialogId: string, text: string): OperatorMessage {
  requireDialog(dialogId);
  const now = new Date();
  const id = `m-op-${++_msgSeq}`;
  const timeLabel = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const msg: OperatorMessage = {
    id,
    dialogId,
    senderType: 'operator',
    text,
    timeLabel,
    createdAt: now.toISOString(),
  };
  MOCK_MESSAGES.push(msg);
  createMockOutbox(dialogId, id);
  createMockAuditEvent(dialogId, 'dialog.message_sent', { messageId: id, textPreview: text.slice(0, 80) });
  return { ...msg };
}

export function mockTransfer(dialogId: string, target: string, targetType: string, reason?: string): OperatorDialog {
  const dialog = requireDialog(dialogId);
  dialog.status = 'escalated';
  dialog.statusLabel = 'Передан';
  dialog.assignedOperatorName = target;
  dialog.updatedAt = new Date().toISOString();
  createMockAuditEvent(dialogId, 'dialog.transferred', { target, targetType, reason: reason ?? null });
  return { ...dialog };
}

const ESCALATION_SEQUENCE = [
  'Руководитель магазина',
  'Учредитель / ответственный',
  'Другой работающий магазин',
  'Форма обратной связи',
];

export function mockEscalate(dialogId: string, reason?: string): OperatorDialog {
  const dialog = requireDialog(dialogId);
  const currentIdx = ESCALATION_SEQUENCE.indexOf(dialog.assignedOperatorName);
  const nextTarget =
    currentIdx >= 0 && currentIdx < ESCALATION_SEQUENCE.length - 1
      ? ESCALATION_SEQUENCE[currentIdx + 1]
      : ESCALATION_SEQUENCE[0];
  const now = new Date();
  dialog.status = 'escalated';
  dialog.statusLabel = 'Эскалирован';
  dialog.assignedOperatorName = nextTarget;
  dialog.updatedAt = now.toISOString();
  (dialog as OperatorDialog & { escalatedAt?: string }).escalatedAt = now.toISOString();
  (dialog as OperatorDialog & { escalationReason?: string | null }).escalationReason = reason ?? null;
  createMockAuditEvent(dialogId, 'dialog.escalated', { escalatedTo: nextTarget, reason: reason ?? null });
  return { ...dialog };
}

export function mockClose(dialogId: string, reason: string): OperatorDialog {
  const dialog = requireDialog(dialogId);
  dialog.status = 'closed';
  dialog.statusLabel = 'Закрыт';
  dialog.updatedAt = new Date().toISOString();
  createMockAuditEvent(dialogId, 'dialog.closed', { reason });
  return { ...dialog };
}

// ─── Mock Telegram webhook demo ──────────────────────────────────────────────

let _demoDialogSeq = 2000;

export function runMockTelegramDemo(): TelegramDemoResult {
  const now = new Date();
  const timeLabel = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const dialogId = `D-DEMO-${++_demoDialogSeq}`;

  const dialog: OperatorDialog = {
    id: dialogId,
    clientName: 'Елена',
    phone: '+7 916 234-45-67',
    channel: 'telegram',
    shopName: 'м. Солнцево',
    statusLabel: 'Ожидает ответа',
    status: 'queued',
    source: 'zov-mebel.ru / utm: cpc',
    elapsedLabel: 'только что',
    assignedOperatorName: 'Ирина П.',
    unreadCount: 1,
    updatedAt: now.toISOString(),
    createdAt: now.toISOString(),
    slaStatus: 'ok',
  };

  const messages: OperatorMessage[] = [
    {
      id: `m-bot-${_demoDialogSeq}`,
      dialogId,
      senderType: 'bot',
      text: 'Здравствуйте. Выберите, куда направить обращение: магазин или отдел сервиса.',
      timeLabel,
      createdAt: now.toISOString(),
    },
    {
      id: `m-client-${_demoDialogSeq}`,
      dialogId,
      senderType: 'client',
      text: 'Магазин. Хочу уточнить стоимость кухни.',
      timeLabel,
      createdAt: now.toISOString(),
    },
  ];

  MOCK_DIALOGS.unshift(dialog);
  MOCK_MESSAGES.push(...messages);
  MOCK_CLIENT_CARDS.push({
    dialogId,
    phone: dialog.phone,
    name: dialog.clientName,
    shopName: dialog.shopName,
    source: dialog.source,
    sourceLabel: 'Сайт: https://grace-mebel.ru',
    pageUrl: 'https://grace-mebel.ru',
    assignedOperatorName: dialog.assignedOperatorName,
    crmFound: true,
    crmClientId: 1,
    channel: dialog.channel,
    statusLabel: dialog.statusLabel,
  });

  const crm = {
    phone: '+79162344567',
    found: true,
    crmClientId: 1,
    crmLeadId: 101,
  };

  const auditEvents = [
    createMockAuditEvent(dialogId, 'webhook.telegram_received', {
      updateId: Date.now(),
      chatId: 'demo-chat',
      site: 'zov-mebel.ru',
    }),
    createMockAuditEvent(dialogId, 'dialog.created', {
      channel: 'telegram',
      shopName: dialog.shopName,
      assignedOperatorName: dialog.assignedOperatorName,
    }),
    createMockAuditEvent(dialogId, 'crm.client_found', crm),
  ];

  return {
    dialog: { ...dialog },
    messages,
    auditEvents,
    crm,
  };
}
